// <OrgFiltersSidebar> — Org View Step 3 (T-049, plan §6.1 / spec §5).
//
// 320-px sticky sidebar with the four filter controls (Search, Team,
// Location, Status). Pure presentation — receives the current filters
// and the `available` dropdown options as props, and notifies the
// parent via `onChange*` callbacks. URL persistence is owned by
// `useOrgViewFilters` (the parent <OrgView> hooks them together).
//
// Server contract (T-047) accepts `status` in {'active','inactive','all'}.
// The spec §5.5 lists additional derived statuses (On leave, On
// probation, Notice given, Pre-boarding) that depend on data the home
// endpoint doesn't yet aggregate; logged as a follow-up. The sidebar
// today renders the supported set: All Statuses / Active / Inactive.

import { Search, ChevronDown } from 'lucide-react';
import type { OrgViewFilters } from '@pa/shared';

export interface OrgFiltersSidebarProps {
  filters: OrgViewFilters;
  availableTeams: Array<{ id: string; name: string }>;
  availableSites: Array<{ id: string; name: string }>;
  onSearchChange: (next: string) => void;
  onTeamChange: (next: string) => void;
  onSiteChange: (next: string) => void;
  onStatusChange: (next: 'active' | 'inactive' | 'all') => void;
}

export function OrgFiltersSidebar({
  filters,
  availableTeams,
  availableSites,
  onSearchChange,
  onTeamChange,
  onSiteChange,
  onStatusChange,
}: OrgFiltersSidebarProps) {
  return (
    <aside
      data-testid="org-filters-sidebar"
      className="w-80 bg-white rounded-3xl border border-slate-200 shadow-sm p-8 flex flex-col gap-8 h-fit sticky top-0"
    >
      <h2 className="text-[11px] font-bold text-slate-400 uppercase tracking-[0.2em]">
        Filters
      </h2>

      {/* Search ────────────────────────────────────────────────── */}
      <div>
        <label
          htmlFor="org-filter-search"
          className="block text-xs font-bold text-slate-600 mb-2 ml-1"
        >
          Search
        </label>
        <div className="relative">
          <Search
            size={14}
            aria-hidden
            className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none"
          />
          <input
            id="org-filter-search"
            type="search"
            value={filters.search}
            onChange={(e) => onSearchChange(e.target.value)}
            placeholder="Search employees..."
            data-testid="org-filter-search"
            className="w-full bg-slate-50 border border-slate-200 rounded-xl pl-10 pr-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#6344C6]/10"
          />
        </div>
      </div>

      {/* Team ──────────────────────────────────────────────────── */}
      <FilterSelect
        id="org-filter-team"
        label="Team"
        value={filters.teamId}
        onChange={onTeamChange}
        defaultOption={{ value: 'all', label: 'All Teams' }}
        options={availableTeams.map((t) => ({ value: t.id, label: t.name }))}
        testId="org-filter-team"
      />

      {/* Location ─────────────────────────────────────────────── */}
      <FilterSelect
        id="org-filter-site"
        label="Location"
        value={filters.siteId}
        onChange={onSiteChange}
        defaultOption={{ value: 'all', label: 'All Locations' }}
        options={availableSites.map((s) => ({ value: s.id, label: s.name }))}
        testId="org-filter-site"
      />

      {/* Status ───────────────────────────────────────────────── */}
      <FilterSelect
        id="org-filter-status"
        label="Status"
        value={filters.status}
        onChange={(v) => onStatusChange(v as 'active' | 'inactive' | 'all')}
        defaultOption={{ value: 'all', label: 'All Statuses' }}
        options={[
          { value: 'active', label: 'Active' },
          { value: 'inactive', label: 'Inactive' },
        ]}
        testId="org-filter-status"
      />
    </aside>
  );
}

function FilterSelect({
  id,
  label,
  value,
  onChange,
  defaultOption,
  options,
  testId,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (next: string) => void;
  defaultOption: { value: string; label: string };
  options: Array<{ value: string; label: string }>;
  testId: string;
}) {
  return (
    <div>
      <label
        htmlFor={id}
        className="block text-xs font-bold text-slate-600 mb-2 ml-1"
      >
        {label}
      </label>
      <div className="relative">
        <select
          id={id}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          data-testid={testId}
          className="w-full appearance-none bg-slate-50 border border-slate-200 rounded-xl pl-4 pr-10 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#6344C6]/10"
        >
          <option value={defaultOption.value}>{defaultOption.label}</option>
          {options.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
        <ChevronDown
          size={16}
          aria-hidden
          className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none"
        />
      </div>
    </div>
  );
}
